import "./RevenueCheckpoint.css";
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, ArrowRight, ChevronDown, ChevronUp, History, RefreshCw, Sparkles } from 'lucide-react';
import {
  Area, AreaChart, CartesianGrid, Label, Line, ReferenceArea, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { dedupFetch } from '../utils/dedupFetch';
import SegmentOptimizerChat from './SegmentOptimizerChat';

const API_BASE = 'https://dashboard-dot-primeval-gear-417120.ue.r.appspot.com';
const KLAVIYO_API_BASE = 'https://mi-chat-892931479853.us-central1.run.app';
const getCompanyId = (c) => c?.CompanyId || c?.companyId || c?.company_id || c?.id || '';

const money = (value) => value == null || Number.isNaN(Number(value))
  ? '—'
  : `$${Math.round(Number(value)).toLocaleString()}`;
const number = (value, digits = 0) => value == null || Number.isNaN(Number(value))
  ? '—'
  : Number(value).toLocaleString(undefined, { maximumFractionDigits: digits });
const percent = (value, isRatio = true) => value == null || Number.isNaN(Number(value))
  ? '—'
  : `${number(isRatio ? Number(value) * 100 : value, 1)}%`;
const numericOrNull = (value) => value == null || Number.isNaN(Number(value)) ? null : Number(value);

function formatLastRun(date) {
  return date.toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
  });
}

function findMetric(components, key) {
  return Object.values(components || {})
    .flatMap((group) => group?.metrics || [])
    .find((metric) => metric.key === key);
}

function metricValue(metric) {
  if (!metric || metric.value == null) return '—';
  if (metric.unit === '$') return money(metric.value);
  if (metric.unit === '%') return `${number(metric.value, 1)}%`;
  if (metric.unit === 'days') return `${number(metric.value)} days`;
  if (metric.unit === 'x/yr') return `${number(metric.value, 1)}×`;
  return number(metric.value, 1);
}

function benchmarkLabel(metric) {
  if (!metric || metric.benchmark == null) return 'Benchmark not available';
  if (metric.unit === '$') return `Benchmark ${money(metric.benchmark)}`;
  if (metric.unit === '%') return `Benchmark ${number(metric.benchmark, 1)}%`;
  if (metric.unit === 'days') return `Benchmark ${number(metric.benchmark)} days`;
  if (metric.unit === 'x/yr') return `Benchmark ${number(metric.benchmark, 1)}×`;
  return `Benchmark ${number(metric.benchmark, 1)}`;
}

function Delta({ value, unit = '%', lowerIsBetter = false }) {
  if (value == null || Number.isNaN(Number(value))) {
    return <span className="rc2-delta rc2-delta--neutral" title="Month-over-month change unavailable">—</span>;
  }
  const numeric = Number(value);
  const positive = lowerIsBetter ? numeric < 0 : numeric > 0;
  const neutral = numeric === 0;
  const suffix = unit === 'days' ? ' days' : unit === 'pts' ? ' pts' : '%';
  return (
    <span className={`rc2-delta ${neutral ? 'rc2-delta--neutral' : positive ? 'rc2-delta--positive rc2-delta--chip' : 'rc2-delta--negative'}`}>
      {numeric > 0 ? '↑' : numeric < 0 ? '↓' : '→'} {number(Math.abs(numeric), 1)}{suffix} vs. previous period
    </span>
  );
}

function MetricDetails({ metrics = [] }) {
  const documentedMetrics = metrics.filter((metric) => metric?.why || metric?.how);
  if (!documentedMetrics.length) return null;

  return (
    <details className="rc2-metric-details">
      <summary>Why this matters</summary>
      <div className="rc2-metric-details-body">
        {documentedMetrics.map((metric) => (
          <div key={metric.key}>
            {documentedMetrics.length > 1 && <strong>{metric.label}</strong>}
            {metric.why && <p>{metric.why}</p>}
            {metric.how && <small>How it’s measured: {metric.how}</small>}
          </div>
        ))}
      </div>
    </details>
  );
}

function CycleCard({ step, title, children, className = '', metrics = [] }) {
  return (
    <article className={`rc2-cycle-card ${className}`}>
      <span className="rc2-cycle-step">{step}</span>
      <h3>{title}</h3>
      {children}
      <MetricDetails metrics={metrics} />
    </article>
  );
}

function SupportingMetric({ label, metric, value, note }) {
  return (
    <div className="rc2-support-item">
      <span className="rc2-support-label">{label}</span>
      <strong>{value ?? metricValue(metric)}</strong>
      <Delta value={null} />
      <span className="rc2-benchmark">{note || benchmarkLabel(metric)}</span>
    </div>
  );
}

export default function RevenueCheckpoint({ companyInfo, token: propToken, onNavigate, onBackfillStatus }) {
  const [summaryData, setSummaryData] = useState(null);
  const [opportunities, setOpportunities] = useState(null);
  const [healthData, setHealthData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastRun, setLastRun] = useState(null);
  const [klaviyoConnected, setKlaviyoConnected] = useState(null);
  const [narrative, setNarrative] = useState(null);
  const [narrativeLoading, setNarrativeLoading] = useState(false);
  const [isDiagnosisExpanded, setIsDiagnosisExpanded] = useState(false);
  // Segment Optimizer chat (same drawer component as Revenue Finder). The key
  // forces a fresh conversation each time the user re-opens it from here.
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [chatKey, setChatKey] = useState(0);
  const openChat = () => { setChatKey((k) => k + 1); setIsChatOpen(true); };
  const companyId = getCompanyId(companyInfo);
  const onBackfillStatusRef = useRef(onBackfillStatus);
  onBackfillStatusRef.current = onBackfillStatus;

  useEffect(() => {
    if (!companyId || !propToken) return undefined;
    let cancelled = false;
    dedupFetch(`${KLAVIYO_API_BASE}/klaviyo/status?company=${encodeURIComponent(companyId)}`, {
      headers: { Authorization: `Bearer ${propToken}` },
    })
      .then((response) => response.ok ? response.json() : null)
      .then((data) => { if (!cancelled && data) setKlaviyoConnected(Boolean(data.connected)); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [companyId, propToken]);

  const runCheckpoint = useCallback(async (forceRefresh = false) => {
    if (!companyId || !propToken) return;
    setLoading(true);
    setError(null);
    try {
      const headers = { Authorization: `Bearer ${propToken}` };
      const [healthRes, summaryRes, opportunitiesRes] = await Promise.all([
        fetch(`${API_BASE}/api/dashboard/checkpoint/${forceRefresh ? 'refresh' : 'health-score'}?company=${encodeURIComponent(companyId)}`, {
          method: forceRefresh ? 'POST' : 'GET', headers,
        }),
        dedupFetch(`${API_BASE}/revenue-finder/summary?company=${encodeURIComponent(companyId)}`, { headers }),
        // Same endpoint Revenue Finder uses for its lever cards — reused here
        // only to build a range ("money on the table"), never a single
        // summed total, since a customer can appear in more than one lever
        // and we cannot dedupe across levers from the frontend.
        dedupFetch(`${API_BASE}/revenue-finder/opportunities?company=${encodeURIComponent(companyId)}&week=current`, { headers }).catch(() => null),
      ]);
      if (!healthRes.ok) throw new Error(`Checkpoint could not load (${healthRes.status})`);
      setHealthData(await healthRes.json());
      setSummaryData(summaryRes.ok ? await summaryRes.json() : null);
      setOpportunities(opportunitiesRes?.ok ? await opportunitiesRes.json() : null);
      setLastRun(new Date());
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  }, [companyId, propToken]);

  useEffect(() => { runCheckpoint(); }, [runCheckpoint]);

  // The weekly brief is generated and cached by the existing Checkpoint
  // narrative endpoint. It is intentionally a separate request: the health
  // score can still load when the AI brief is temporarily unavailable.
  useEffect(() => {
    // The endpoint caches the generated brief for 24 hours. Do not call it
    // until the monthly summary is available too, otherwise a partial payload
    // without goal/projection would be cached as this week's diagnosis.
    if (!healthData || !summaryData?.mainMetrics || !companyId || !propToken) {
      setNarrative(null);
      setNarrativeLoading(false);
      return undefined;
    }

    const controller = new AbortController();
    const narrativeMain = summaryData.mainMetrics;
    const narrativeGoal = numericOrNull(narrativeMain.monthlyRevenueGoal);
    const narrativeActual = numericOrNull(narrativeMain.currentMonthRevenue);
    const narrativeRawProjection = numericOrNull(narrativeMain.projection);
    const narrativeProjection = narrativeRawProjection != null && narrativeRawProjection > 0
      ? narrativeRawProjection
      : null;
    const narrativeDaysRemaining = numericOrNull(narrativeMain.daysRemaining);
    setNarrative(null);
    setNarrativeLoading(true);
    setIsDiagnosisExpanded(false);

    fetch(`${API_BASE}/api/dashboard/checkpoint/narrative`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${propToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        company_id: companyId,
        score: healthData.score,
        raw: healthData.raw || {},
        components: healthData.components || {},
        monthly_outlook: {
          goal: narrativeGoal,
          actual: narrativeActual,
          projection: narrativeProjection,
          projected_gap: narrativeGoal != null && narrativeProjection != null
            ? narrativeProjection - narrativeGoal
            : null,
          days_remaining: narrativeDaysRemaining == null ? null : Math.max(narrativeDaysRemaining, 0),
        },
      }),
      signal: controller.signal,
    })
      .then((response) => response.ok ? response.json() : null)
      .then((data) => {
        if (controller.signal.aborted) return;
        const hasDiagnosis = typeof data?.diagnosis === 'string' && data.diagnosis.trim();
        const hasDetail = typeof data?.whats_happening === 'string' && data.whats_happening.trim();
        setNarrative(hasDiagnosis || hasDetail ? data : null);
      })
      .catch(() => {
        // The UI deliberately stays honest below rather than creating a local
        // pseudo-diagnosis from partial metrics.
      })
      .finally(() => {
        if (!controller.signal.aborted) setNarrativeLoading(false);
      });

    return () => controller.abort();
  }, [healthData, summaryData, companyId, propToken]);

  const main = summaryData?.mainMetrics || {};
  const raw = healthData?.raw || {};
  const components = healthData?.components || {};
  const detail = summaryData?.detailMetrics || {};
  const revenueAtRisk = healthData?.revenue_at_risk || {};
  const atRiskCount = numericOrNull(revenueAtRisk.customer_count);

  const goalType = main.goalType || null;
  const isEstimatedGoal = goalType === 'estimated';
  const isManualGoal = goalType === 'manual';
  const goalBasis = main.goalBasis || null;
  const estimatedGrowthLabel = goalBasis?.growth_pct != null
    ? ` + ${goalBasis.growth_pct}% growth`
    : '';
  const goalSourceLabel = isEstimatedGoal
    ? `Estimated from your last ${goalBasis?.months_used || 'recent'} months of sales${estimatedGrowthLabel}`
    : isManualGoal
      ? 'Set by you'
      : null;
  const goal = numericOrNull(main.monthlyRevenueGoal);
  const actual = numericOrNull(main.currentMonthRevenue);
  // Aviso de frescura del dato (pedido de Alejo 2026-10-29): el sync de
  // Shopify->BigQuery corre cada 6h, asi que este numero puede tener hasta
  // ~6h de atraso frente a Shopify en vivo. Mismo patron que Shopify usa con
  // su propio "Last refreshed" en Analytics.
  const dataUpdatedAt = summaryData?.dataUpdatedAt ? new Date(summaryData.dataUpdatedAt) : null;
  const dataUpdatedLabel = dataUpdatedAt && !Number.isNaN(dataUpdatedAt.getTime())
    ? `Data updated ${formatLastRun(dataUpdatedAt)}`
    : null;
  // Honesty guard: a forecast of $0 or a negative amount is not a real
  // projection (seen in the wild for at least one real store) — treat it the
  // same as "no forecast yet" everywhere downstream instead of surfacing a
  // nonsensical or misleading number.
  const rawProjection = numericOrNull(main.projection);
  const projection = rawProjection != null && rawProjection > 0 ? rawProjection : null;
  // Same honesty guard applied to the floor/ceiling of the projection range
  // (added by the backend alongside the single-number `projection`, which did
  // not change value). Data confirmed a single number can miss by up to -40%
  // early in the month, so product decision (Alejo) is to ALWAYS surface a
  // range, never a lone number — this is the only place that range is read.
  // Older cached responses (or `method !== "forecast"`) may not carry
  // low/high yet; in that case everything below falls back to the single
  // `projection` number/line exactly as before.
  const rawProjectionLow = numericOrNull(main.projectionLow);
  const rawProjectionHigh = numericOrNull(main.projectionHigh);
  const projectionLow = rawProjectionLow != null && rawProjectionLow > 0 ? rawProjectionLow : null;
  const projectionHigh = rawProjectionHigh != null && rawProjectionHigh > 0 ? rawProjectionHigh : null;
  const hasProjectionRange = projectionLow != null && projectionHigh != null;
  // Low === high happens when the backend has no confident recent pace to
  // build a range from — render it as a single value rather than a
  // zero-width "range" that reads like a typo.
  const rangeMoneyLabel = (low, high) => (
    Math.round(low) === Math.round(high) ? money(low) : `${money(low)} – ${money(high)}`
  );
  const rawDaysRemaining = numericOrNull(main.daysRemaining);
  const daysRemaining = rawDaysRemaining == null ? null : Math.max(rawDaysRemaining, 0);
  const hasGoal = goal != null && goal > 0;
  const projectedGap = hasGoal && projection != null ? projection - goal : null;
  const remainingToGoal = hasGoal && actual != null ? Math.max(goal - actual, 0) : null;
  const requiredPace = remainingToGoal != null && daysRemaining > 0 ? remainingToGoal / daysRemaining : null;
  const achieved = hasGoal && actual != null && actual >= goal;
  const projectionRatio = hasGoal && projection != null ? projection / goal : null;
  // Direccion de las etiquetas "Projected"/"Goal" en el grafico: cuando las
  // dos lineas terminan cerca (se solaparian), se separan 22px cada una en
  // direccion OPUESTA a la otra. Antes esa direccion era fija (Projected
  // siempre arriba, Goal siempre abajo) sin importar cual valor es mayor —
  // si la proyeccion terminaba MAS BAJA que la meta (off track), la etiqueta
  // igual quedaba arriba, dando la impresion visual contraria a los numeros.
  // Ahora la direccion depende de cual linea termina mas alta en $.
  // With a range, the in-chart label sits on the ceiling (top) line, so
  // collision-with-Goal is judged against the ceiling value, not the
  // midpoint — falls back to the single projection number when no range.
  const forecastLabelAnchor = hasProjectionRange ? projectionHigh : projection;
  const labelsClose = hasGoal && forecastLabelAnchor != null
    && Math.abs(forecastLabelAnchor - goal) < Math.max(goal, forecastLabelAnchor) * 0.12;
  const projectionEndsHigher = hasGoal && forecastLabelAnchor != null ? forecastLabelAnchor >= goal : true;
  // El grafico mide 280px de alto (margin top 16). Si la meta o la
  // proyeccion terminan cerca del techo o el piso del eje, empujar la
  // etiqueta 22px hacia afuera la saca del area visible (se corta contra el
  // borde del SVG). Se limita la posicion final para que la pill siempre
  // quede dentro del canvas.
  const clampLabelY = (y) => Math.min(Math.max(y, 16), 264);
  const gapNode = (amount) => <strong className={`rc2-gap-value rc2-gap-value--${amount >= 0 ? 'positive' : 'negative'}`}>{money(Math.abs(amount))}</strong>;
  const outlook = !hasGoal
    ? { key: 'neutral', label: 'Goal needed', copy: 'Set a monthly goal to measure your projected finish' }
    : achieved
      // Actual sales already met/passed the goal — this is true regardless of
      // whether a forecast is available, so it must win over the "forecast
      // unavailable" branch below.
      ? { key: 'achieved', label: 'Goal achieved', copy: <>{money(actual)} sold against a {money(goal)} goal</> }
      : projection == null
        ? { key: 'neutral', label: 'Forecast pending', copy: 'Your goal is set — we’ll show a projection once there’s enough data' }
        : projectionRatio > 1.05
        ? { key: 'ahead', label: 'Ahead', copy: <>{gapNode(projectedGap)} above your {money(goal)} goal</> }
        : projectionRatio >= 0.98
          ? { key: 'track', label: 'On track', copy: <>{gapNode(projectedGap)} {projectedGap >= 0 ? 'above' : 'below'} your {money(goal)} goal</> }
          : { key: 'risk', label: 'Off track', copy: <>{gapNode(projectedGap)} below your {money(goal)} goal</> };

  const repeat12m = findMetric(components, 'p_alive');
  const historicalRepeat = findMetric(components, 'repeat_rate');
  const t2sp = findMetric(components, 't2sp');
  const cadence = findMetric(components, 'champion_cadence');
  const cohort60day = findMetric(components, 'cohort_60day');
  const frequency = findMetric(components, 'frequency');
  const clv = findMetric(components, 'clv_12m');
  const highValue = findMetric(components, 'loyal_pct');

  const watchedMetrics = [
    atRiskCount != null && {
      label: 'Customers slipping',
      value: `${number(atRiskCount)} customers`,
      note: raw.at_risk_pct != null ? `${percent(raw.at_risk_pct)} of active customers` : null,
    },
    repeat12m?.value != null && {
      label: '12-month repeat rate',
      value: metricValue(repeat12m),
      note: benchmarkLabel(repeat12m),
    },
    t2sp?.value != null && {
      label: 'Time to second purchase',
      value: metricValue(t2sp),
      note: benchmarkLabel(t2sp),
    },
  ].filter(Boolean).slice(0, 3);

  // A small gap (outlook "track") reads as trivial if we headline the raw
  // dollar amount — "$548 to close" doesn't create urgency. In that state we
  // point at what's easy to recover this week instead of the gap itself.
  const isCloseButNotThere = outlook.key === 'track' && projectedGap != null && projectedGap < 0;

  // "Money on the table" range, from the same weekly opportunities Revenue
  // Finder shows. We deliberately do NOT sum every lever into one number —
  // the same customer can qualify for more than one lever, and the frontend
  // has no way to dedupe across them. Instead: a floor (the single biggest
  // lever alone — always real, never double-counted) and a ceiling (the sum
  // of everything listed, labeled as a range so it reads as "up to", not a
  // promised total).
  const opportunityImpacts = (opportunities?.weeklyOpportunities || [])
    .map((item) => numericOrNull(item?.impact))
    .filter((value) => value != null && value > 0);
  const opportunityTotal = opportunityImpacts.length
    ? opportunityImpacts.reduce((sum, value) => sum + value, 0)
    : null;
  // Mirrors Revenue Finder's hero range exactly (RevenueFinder.jsx:313-315:
  // 75%-100% of the summed impact, rounded to the nearest $1,000) so both
  // screens show the identical number for the identical underlying data.
  const opportunityFloor = opportunityTotal ? Math.round(opportunityTotal * 0.75 / 1000) * 1000 : null;
  const opportunityCeiling = opportunityTotal ? Math.round(opportunityTotal / 1000) * 1000 : null;
  const opportunityCount = opportunityImpacts.length;
  const hasOpportunityRange = opportunityFloor != null && opportunityCeiling != null && opportunityFloor > 0;
  // Prefer the forecast shortfall when it exists. If the forecast is still
  // pending (including an invalid $0 forecast), the amount left against the
  // actual sales-to-date is still a real monthly gap and lets the brief give
  // useful, honest context for the opportunities already identified.
  const monthlyGap = projectedGap != null && projectedGap < 0
    ? Math.abs(projectedGap)
    : !achieved && remainingToGoal != null && remainingToGoal > 0
      ? remainingToGoal
      : null;
  // Only show a "would close" claim when the monthly gap fits within the
  // stated opportunity range. Otherwise the fallback below gives the amount
  // that would still remain instead of implying an impossible percentage.
  const recoveryPct = (hasOpportunityRange && !achieved && !isCloseButNotThere && monthlyGap != null && monthlyGap <= opportunityCeiling)
    ? Math.max(1, Math.round((monthlyGap / opportunityCeiling) * 100))
    : null;
  const remainingAfterOpportunity = (hasOpportunityRange && !achieved && !isCloseButNotThere && monthlyGap != null && monthlyGap > opportunityCeiling)
    ? monthlyGap - opportunityCeiling
    : null;
  // For the no-gap states (achieved / ahead of goal), there's nothing to
  // "close" so a computed % doesn't apply. Instead show a fixed 10%
  // reference against the ceiling — NOT framed as a requirement or a
  // promise to close anything (which could be false), just an illustration
  // of remaining upside: 10% of a real number is always arithmetically true.
  const upsidePct = 10;
  const upsideAmount = (hasOpportunityRange && (achieved || (projectedGap != null && projectedGap >= 0)))
    ? Math.round((opportunityCeiling * upsidePct) / 100)
    : null;

  const briefEyebrow = isCloseButNotThere
    ? 'You’re close — here’s what can push you over'
    : hasOpportunityRange
      ? 'How much is on the table this week?'
      : 'How much do you need to recover this month?';
  const briefHeadline = !hasGoal
    ? 'Your weekly revenue diagnosis'
    : achieved
      ? `${money(actual - goal)} above your goal so far`
      : projectedGap == null
        ? hasOpportunityRange
          ? (opportunityCount === 1 || Math.round(opportunityFloor) === Math.round(opportunityCeiling)
            ? `${money(opportunityFloor)} identified this week`
            : `${money(opportunityFloor)}–${money(opportunityCeiling)} identified this week`)
          : 'Your weekly revenue diagnosis'
        : projectedGap < 0
          ? isCloseButNotThere
            ? `Just ${money(Math.abs(projectedGap))} away from goal`
            : hasOpportunityRange
              ? (opportunityCount === 1 || Math.round(opportunityFloor) === Math.round(opportunityCeiling)
                ? `${money(opportunityFloor)} identified this week`
                : `${money(opportunityFloor)}–${money(opportunityCeiling)} identified this week`)
              : `${money(Math.abs(projectedGap))} to close this month`
          : `${money(projectedGap)} above your goal this month`;
  // Pulled out of the paragraph below and shown as its own prominent line
  // next to the headline — the "how much would it take" answer is the most
  // actionable part of this brief and was getting lost at the end of a long
  // disclaimer sentence.
  const recoveryCallout = recoveryPct != null
    ? `Converting just ~${recoveryPct}% of this would close this month's ${money(monthlyGap)} gap.`
    : remainingAfterOpportunity != null
      ? `Even converting the full estimated opportunity would still leave ~${money(remainingAfterOpportunity)} to close this month.`
    : upsideAmount != null
      ? `Converting just ${upsidePct}% of this would add another ~${money(upsideAmount)} on top of your goal.`
      : null;
  const briefContext = !hasGoal
    ? 'Your goal is not set yet. The diagnosis below still tracks the customer signals shaping revenue.'
    : isCloseButNotThere
      ? 'This is easily within reach — a single play activated this week can close the rest of the gap.'
      : (!achieved && !isCloseButNotThere && hasOpportunityRange)
        ? `Estimated opportunity across ${opportunityCount} play${opportunityCount === 1 ? '' : 's'}, typically realized over 30–60 days as customers respond — not a same-week payout. Some customers may qualify for more than one play, so treat the top of the range as a ceiling, not a guarantee.`
        : projectedGap == null && !achieved
        ? 'Your forecast is still forming. These are the customer opportunities already identified for this month.'
        : null;
  const hasNarrativeDiagnosis = typeof narrative?.diagnosis === 'string' && narrative.diagnosis.trim().length > 0;
  const hasNarrativeDetail = hasNarrativeDiagnosis
    && typeof narrative?.whats_happening === 'string'
    && narrative.whats_happening.trim().length > 0;

  // Plays summary + top opportunities (backend fields added alongside
  // diagnosis/whats_happening/action in the same /checkpoint/narrative
  // response — see checkpoint-plays-summary branch on api-master).
  // available: false -> we could not read the source (neutral state, not
  // "0 plays"). available: true + active_plays_count: 0 -> genuinely no
  // active plays (empty state). campaigns_run/correlated_revenue_30d can be
  // individually null (partial data) even when the play itself is fine.
  const playsSummary = narrative?.active_plays_summary || null;
  const playsAvailable = playsSummary?.available === true;
  const playsList = Array.isArray(playsSummary?.plays) ? playsSummary.plays : [];
  const activePlaysCount = numericOrNull(playsSummary?.active_plays_count);
  const totalCorrelatedRevenue = numericOrNull(playsSummary?.total_correlated_revenue_30d);
  const attributionLabel = playsSummary?.attribution_label || 'correlational';
  const PLAYS_NAME_PREVIEW = 3;
  const previewPlayNames = playsList.slice(0, PLAYS_NAME_PREVIEW).map((play) => play?.name).filter(Boolean);
  const extraPlaysCount = Math.max(playsList.length - previewPlayNames.length, 0);
  const topOpportunities = Array.isArray(narrative?.top_opportunities) ? narrative.top_opportunities.slice(0, 3) : [];
  // Fix 5 (ronda 2): the Plays/opportunities block should be able to show
  // even when whats_happening is empty — gate the detail section on either
  // the diagnosis text OR the plays/opportunities data being present.
  const hasPlaysOrOpportunitiesContent = !!playsSummary || topOpportunities.length > 0;
  const hasDetailSection = hasNarrativeDetail || hasPlaysOrOpportunitiesContent;

  // Backend fields added in parallel (dashboard_api.py): raw.customer_composition
  // (total_customers/vip_count/slipping_count) and raw.aov_by_type
  // (aov_first_order/aov_repeat_orders/insufficient_data/sample_size). Both
  // are consumed null-safely here so this screen works whether or not the
  // backend has shipped them yet.
  const composition = raw.customer_composition || null;
  const hasComposition = Boolean(
    composition
    && composition.total_customers != null
    && composition.vip_count != null
    && composition.slipping_count != null,
  );
  const aovByType = raw.aov_by_type || null;
  const aovInsufficient = !aovByType
    || aovByType.insufficient_data
    || aovByType.aov_first_order == null
    || aovByType.aov_repeat_orders == null;
  const blendedAov = numericOrNull(raw.aov_subtotal) || numericOrNull(raw.aov);
  const highValueNote = hasComposition
    ? `${number(composition.vip_count)} VIP customers, ${percent(highValue?.value != null ? highValue.value / 100 : null)} of your base${raw.loyal_spend_ratio >= 1.5 ? ` · spend ${number(raw.loyal_spend_ratio, 1)}× more than one-time buyers` : ''}`
    : (raw.loyal_spend_ratio >= 1.5
      ? `3+ orders · spend ${number(raw.loyal_spend_ratio, 1)}× more than one-time buyers`
      : benchmarkLabel(highValue));

  const chartData = useMemo(() => {
    const chart = summaryData?.chartData || [];
    if (!chart.length) return [];
    // Backend contract: for the current month, chartData prepends one special
    // cumulative "today" row. It carries the full monthly goal (or 0 if no
    // goal) and projection === revenue; weekly rows carry a per-week goal (or
    // null). Detect that shape directly, without parsing yearless labels.
    const first = chart[0];
    const firstRevenue = numericOrNull(first?.revenue);
    const firstProjection = numericOrNull(first?.projection);
    // The backend today-row is built from a single source value assigned to
    // both `revenue` and `projection` (see _get_revenue_chart_data: both set
    // from `current_revenue`), so they are exactly equal by construction.
    // Weekly rows compute `revenue` (actual fact sales) and `projection`
    // (prorated forecast yhat) from unrelated sources, so exact equality
    // there would only ever happen by coincidence. This avoids relying on
    // goal comparisons, which break when the weekly goal equals the monthly
    // goal (single-week month) or when there is no goal at all (goal: 0).
    const isTodayPoint = chart.length > 1
      && firstRevenue != null
      && firstProjection === firstRevenue;
    const weekly = isTodayPoint ? chart.slice(1) : chart;
    if (!weekly.length) return [];

    // Backend guarantees weekly.revenue is never null for weeks up to and
    // including today (real value, or 0 as a placeholder) and is null only
    // for future weeks. That lets us find the "today" week structurally,
    // without re-parsing dates.
    let lastActualIndex = -1;
    let cumulativeActual = 0;
    const actualByIndex = weekly.map((item, index) => {
      const weeklyActual = numericOrNull(item.revenue);
      if (weeklyActual == null) return null;
      cumulativeActual += weeklyActual;
      lastActualIndex = index;
      return cumulativeActual;
    });
    const bridgeValue = lastActualIndex >= 0 ? actualByIndex[lastActualIndex] : 0;

    // Raw forecast for future weeks only, accumulated from the bridge point.
    let rawForecastCum = 0;
    const rawFutureByIndex = weekly.map((item, index) => {
      if (index <= lastActualIndex) return null;
      const weeklyForecast = numericOrNull(item.projection);
      rawForecastCum += weeklyForecast != null ? weeklyForecast : 0;
      return rawForecastCum;
    });
    const rawTotal = rawFutureByIndex[weekly.length - 1] || 0;
    // Rescale future increments so a forecast line lands exactly on its
    // target month-end number instead of drifting from it when weekly
    // forecast rows are missing/partial. Shared by the single "projection"
    // line (fallback) and the floor/ceiling range lines — each just plugs in
    // a different target.
    //
    // Honesty guard: we only have real weekly forecast shape to draw a curve
    // when rawTotal > 0. If every future weekly forecast is null/0 (rawTotal
    // === 0) but the target differs from the bridge (i.e. there IS a
    // projected change), a flat line would visually contradict the big
    // number above the chart. Same problem if the target is below the
    // actual-to-date bridge (targetTotal gets clamped to 0, which would also
    // draw a flat/fabricated line). In both cases, don't draw a fabricated
    // future curve — the headline number still communicates the forecast;
    // the chart just won't draw the un-shaped tail for that line.
    const targetDiffers = (targetValue) => targetValue != null
      && Math.round(targetValue) !== Math.round(bridgeValue);

    const buildProjectionSeries = (targetValue, canDrawCurveOverride = null) => {
      if (targetValue == null) return weekly.map(() => null);
      const targetTotal = Math.max(targetValue - bridgeValue, 0);
      const scale = rawTotal > 0 ? targetTotal / rawTotal : 0;
      const canDrawCurve = canDrawCurveOverride != null
        ? canDrawCurveOverride
        : rawTotal > 0 || !targetDiffers(targetValue);
      return weekly.map((item, index) => {
        if (index === lastActualIndex) return bridgeValue; // line starts exactly where the solid line ends
        if (index > lastActualIndex && canDrawCurve) {
          const raw = rawFutureByIndex[index] || 0;
          return bridgeValue + raw * scale;
        }
        return null;
      });
    };

    const projectionSeries = buildProjectionSeries(projection);
    // Floor and ceiling must appear/disappear TOGETHER -- they are a single
    // band, not two independent lines. Deciding canDrawCurve per-series (as
    // projection does) can split them apart: if rawTotal === 0 and only one
    // of the two targets happens to equal the bridge, that one would draw a
    // flat line while the other stayed null, collapsing the shaded band and
    // the "Projected $X-$Y" label with no visual explanation. Decide once,
    // for the pair, using whichever target actually needs a curve.
    const canDrawRange = hasProjectionRange
      ? rawTotal > 0 || !(targetDiffers(projectionLow) || targetDiffers(projectionHigh))
      : null;
    const lowSeries = hasProjectionRange ? buildProjectionSeries(projectionLow, canDrawRange) : null;
    const highSeries = hasProjectionRange ? buildProjectionSeries(projectionHigh, canDrawRange) : null;

    return weekly.map((item, index) => ({
      day: item.day,
      actual: actualByIndex[index],
      projection: projectionSeries[index],
      projectionLow: hasProjectionRange ? lowSeries[index] : null,
      projectionHigh: hasProjectionRange ? highSeries[index] : null,
      goalPace: hasGoal ? goal * ((index + 1) / weekly.length) : null,
      isToday: index === lastActualIndex,
    }));
  }, [summaryData, goal, hasGoal, projection, projectionLow, projectionHigh, hasProjectionRange]);

  const todayChartDay = useMemo(
    () => chartData.find((row) => row.isToday)?.day ?? null,
    [chartData],
  );
  const lastChartDay = chartData.length ? chartData[chartData.length - 1].day : null;

  const hasRealData = Boolean(healthData && (
    raw.repeat_rate > 0 || raw.frequency > 0 || raw.avg_p_alive > 0 || raw.repeat_buyers_count > 0
  ));
  const backfillStatus = healthData?.full_backfill_status;
  const loadingNotice = healthData && (
    backfillStatus === 'queued' || backfillStatus === 'running' ||
    (backfillStatus === 'unknown' && !hasRealData) ||
    (backfillStatus === 'complete' && !hasRealData)
  );

  useEffect(() => {
    if (healthData && onBackfillStatusRef.current) {
      onBackfillStatusRef.current(backfillStatus, hasRealData);
    }
  }, [healthData, backfillStatus, hasRealData]);

  return (
    <div className="rc2-root">
      <header className="rc2-topbar">
        <div className="rc2-topbar-left">
          <div className="rc2-badges">
            <span className="rc2-badge rc2-badge-connected">Shopify connected</span>
            {klaviyoConnected === true && <span className="rc2-badge rc2-badge-connected">Klaviyo connected</span>}
          </div>
          {lastRun && <span className="rc2-lastrun"><History size={14} /> Updated {formatLastRun(lastRun)}</span>}
        </div>
        <button
          type="button"
          className="rc2-run-btn"
          onClick={() => runCheckpoint(true)}
          disabled={loading}
          aria-label={loading ? 'Refreshing checkpoint' : 'Refresh checkpoint'}
        >
          <RefreshCw size={14} className={loading ? 'rc2-spin' : ''} />
          {loading ? 'Refreshing…' : 'Refresh checkpoint'}
        </button>
      </header>

      <main className="rc2-main">
        {error && <div className="rc2-error" role="alert"><AlertTriangle size={16} /> {error}</div>}
        {loading && !healthData && <div className="rc2-loading">Building your checkpoint…</div>}
        {loadingNotice && <div className="checkpoint-loading-notice"><span className="loading-spinner" /> Preparing revenue insights…</div>}

        {summaryData && (
          <section className="rc2-outlook" aria-labelledby="monthly-outlook-title">
            <div className={`rc2-outlook-copy ${outlook.key === 'risk' ? 'rc2-outlook-copy--risk' : (outlook.key === 'track' || outlook.key === 'ahead' || outlook.key === 'achieved') ? 'rc2-outlook-copy--positive' : ''}`}>
              <div className="rc2-decision-heading" id="monthly-outlook-title">How far are you from your goal?</div>
              <span className={`rc2-state rc2-state--${outlook.key}`}>{outlook.label}</span>
              <p className="rc2-outlook-label">Monthly sales goal</p>
              {hasGoal
                ? (
                  <>
                    <h1 className="rc2-outlook-value">{money(goal)}</h1>
                    {goalSourceLabel && (
                      <div className="rc2-goal-source">
                        <span>{goalSourceLabel}</span>
                        <button type="button" className="rc2-inline-link" onClick={() => onNavigate?.('settings')}>Edit</button>
                      </div>
                    )}
                  </>
                )
                : (
                  <>
                    <h1 className="rc2-outlook-value rc2-outlook-value--muted">No goal set yet</h1>
                  </>
                )}
              <p className="rc2-outlook-summary">{outlook.copy}</p>

              <div className="rc2-secondary-figures">
                <div className="rc2-sales-to-date rc2-sales-to-date--emphasis">
                  <span>Sales to date</span>
                  <strong>{money(actual)}</strong>
                  <small>Actual, this month so far</small>
                  {dataUpdatedLabel && (
                    <small className="rc2-data-freshness" title="Synced from Shopify every 6 hours">
                      {dataUpdatedLabel}
                    </small>
                  )}
                </div>
                <div className="rc2-sales-to-date rc2-sales-to-date--emphasis">
                  <span>{achieved ? 'Revenue this month' : 'Projected month-end'}</span>
                  <strong className={
                    achieved || outlook.key === 'track' || outlook.key === 'ahead'
                      ? 'rc2-projection-value rc2-projection-value--good'
                      : outlook.key === 'risk'
                        ? 'rc2-projection-value rc2-projection-value--bad'
                        : 'rc2-projection-value'
                  }>
                    {achieved
                    ? money(actual)
                    : hasProjectionRange
                      ? rangeMoneyLabel(projectionLow, projectionHigh)
                      : projection == null ? '—' : money(projection)}
                    {projection != null && !achieved && outlook.key === 'risk' && (
                      <em className="rc2-projection-flag rc2-projection-flag--bad" aria-label="Below goal">▾ below goal</em>
                    )}
                    {(achieved || ((outlook.key === 'track' || outlook.key === 'ahead') && projection != null)) && (
                      <em className="rc2-projection-flag rc2-projection-flag--good" aria-label="On pace">✓ {achieved ? 'goal met' : 'on pace'}</em>
                    )}
                  </strong>
                  {achieved
                    ? <small>Goal already achieved</small>
                    : (projection == null && !hasProjectionRange)
                      ? <small>Forecast pending — we need a few more weeks of data</small>
                      : hasProjectionRange
                        ? (
                          <small title="Floor and ceiling of where this month is likely to land. A single number can miss by up to 40% early in the month — the range narrows as more days of real sales come in.">
                            Floor–ceiling range · narrows as the month progresses
                          </small>
                        )
                        : (
                          <small title="Based on backtests across stores: typical error is 6–9% by mid-month in normal months, and up to ±20% in months with promos or demand swings.">
                            Estimate — typically within ±7% by mid-month
                          </small>
                        )}
                </div>
              </div>

              <div className="rc2-outlook-minor">
                <div>
                  <span>Required pace</span><strong>{requiredPace == null ? '—' : `${money(requiredPace)}/day`}</strong>
                </div>
                <div>
                  <span>Time remaining</span><strong>{daysRemaining == null ? '—' : `${daysRemaining} days`}</strong>
                </div>
              </div>
            </div>

            <div className="rc2-chart-panel">
              <div className="rc2-chart-heading">
                <div><strong>Revenue progress</strong><span>Actual sales, forecast and the pace needed for your goal</span></div>
                <div className="rc2-chart-key" aria-label="Chart legend">
                  <span><i className="actual" />Actual</span><span><i className="forecast" />{hasProjectionRange ? 'Forecast range' : 'Forecast'}</span>{hasGoal && <span><i className="pace" />Goal pace</span>}
                </div>
              </div>
              {chartData.length ? (
                <ResponsiveContainer width="100%" height={280}>
                  <AreaChart data={chartData} margin={{ top: 16, right: 132, bottom: 0, left: 0 }}>
                    <defs>
                      <linearGradient id="checkpointActual" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#6D28D9" stopOpacity={0.28} />
                        <stop offset="100%" stopColor="#6D28D9" stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid vertical={false} stroke="#EEF0F4" />
                    <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fill: '#707786', fontSize: 11 }} />
                    <YAxis width={52} axisLine={false} tickLine={false} tick={{ fill: '#707786', fontSize: 11 }} tickFormatter={(v) => `$${Math.round(v / 1000)}k`} />
                    <Tooltip
                      formatter={(value, name) => [money(value), name]}
                      content={({ active, payload, label }) => {
                        if (!active || !payload?.length) return null;
                        const isBridge = payload[0]?.payload?.isToday;
                        // On the bridge ("today") point every forecast line
                        // (single projection, or floor/ceiling) is pinned to
                        // the same value as Actual by construction — hide
                        // those duplicate rows there.
                        const forecastNames = ['Forecast', 'Floor', 'Ceiling'];
                        const rows = payload.filter((entry) => entry.value != null && !(isBridge && forecastNames.includes(entry.name)));
                        if (!rows.length) return null;
                        return (
                          <div style={{ background: '#fff', border: '1px solid #E2E5EA', borderRadius: 8, padding: '10px 14px', fontSize: 13 }}>
                            <strong style={{ display: 'block', marginBottom: 4 }}>{label}{isBridge ? ' · Today' : ''}</strong>
                            {rows.map((entry) => (
                              <div key={entry.name} style={{ color: entry.color }}>
                                {`${entry.name}: ${money(entry.value)}`}
                              </div>
                            ))}
                          </div>
                        );
                      }}
                    />
                    {todayChartDay && (
                      <ReferenceLine x={todayChartDay} stroke="#6D28D9" strokeOpacity={0.07} strokeWidth={38} />
                    )}
                    {todayChartDay && (
                      <ReferenceLine x={todayChartDay} stroke="#4B5468" strokeDasharray="3 3">
                        <Label content={({ viewBox }) => (
                          <g>
                            <rect x={viewBox.x - 26} y={2} width={52} height={18} rx={9} fill="#1F2437" />
                            <text x={viewBox.x} y={14.5} fill="#FFFFFF" fontSize={9.5} fontWeight={800} letterSpacing="0.08em" textAnchor="middle">TODAY</text>
                          </g>
                        )} />
                      </ReferenceLine>
                    )}
                    <Area
                      type="monotone" dataKey="actual" name="Actual" stroke="#4C1D95" strokeWidth={3} fill="url(#checkpointActual)" connectNulls
                      dot={({ index, cx, cy, value }) => {
                        const isBridge = chartData[index]?.day === todayChartDay && value != null;
                        if (!isBridge) return <g key={`d${index}`} />;
                        return (
                          <g key={`d${index}`}>
                            <circle cx={cx} cy={cy} r={6} fill="#FFFFFF" stroke="#6D28D9" strokeWidth={3} />
                          </g>
                        );
                      }}
                    />
                    {hasProjectionRange ? (
                      <>
                        {/* Shaded band between floor and ceiling — Recharts
                            renders a range fill when `dataKey` is a function
                            returning a two-element [low, high] tuple instead
                            of a single value. */}
                        <Area
                          type="monotone"
                          dataKey={(point) => (point.projectionLow != null && point.projectionHigh != null
                            ? [point.projectionLow, point.projectionHigh]
                            : [null, null])}
                          name="Forecast range"
                          stroke="none"
                          fill="#6B4EFF"
                          fillOpacity={0.1}
                          connectNulls
                          legendType="none"
                          tooltipType="none"
                        />
                        <Line
                          type="monotone"
                          dataKey="projectionLow"
                          name="Floor"
                          stroke="#B6A8F7"
                          strokeWidth={1.5}
                          strokeDasharray="4 4"
                          dot={false}
                        />
                        <Line
                          type="monotone"
                          dataKey="projectionHigh"
                          name="Ceiling"
                          stroke="#6B4EFF"
                          strokeWidth={2.5}
                          strokeDasharray="8 6"
                          dot={false}
                          // Single combined "Projected $low–$high" pill on the
                          // ceiling (top) line — one label for the whole
                          // range rather than two separate pills, so the
                          // collision logic below only ever has to reason
                          // about this one line vs. the Goal pace line,
                          // exactly like the old single-projection case did.
                          label={({ viewBox, value, index }) => {
                            if (index !== chartData.length - 1 || value == null) return null;
                            const lowValue = chartData[index]?.projectionLow;
                            const txt = lowValue != null
                              ? `Projected ${rangeMoneyLabel(lowValue, value)}`
                              : `Projected ${money(value)}`;
                            const w = txt.length * 6.4 + 16;
                            const rawOffset = labelsClose
                              ? (projectionEndsHigher ? viewBox.y - 22 : viewBox.y + 22)
                              : viewBox.y - 12;
                            const yOffset = clampLabelY(rawOffset);
                            return (
                              <g>
                                <rect x={viewBox.x + 4} y={yOffset - 10} width={w} height={20} rx={10} fill="#F1EAFE" stroke="#D9CCFA" />
                                <text x={viewBox.x + 4 + w / 2} y={yOffset + 4} fill="#6D28D9" fontSize={11} fontWeight={700} textAnchor="middle">{txt}</text>
                              </g>
                            );
                          }}
                        />
                      </>
                    ) : (
                      <Line
                        type="monotone"
                        dataKey="projection"
                        name="Forecast"
                        stroke="#6B4EFF"
                        strokeWidth={2.5}
                        strokeDasharray="8 6"
                        dot={false}
                        // NOTE: recharts 3.3.0 renders <Label> children of <Line> once,
                        // without a per-point `index`, so `index !== chartData.length - 1`
                        // was always comparing against `undefined` and never matched —
                        // the label silently never rendered. The `label` prop instead
                        // routes through LabelList, which clones one element per data
                        // point and does pass `index`, so this same content function
                        // now actually finds the last point.
                        label={({ viewBox, value, index }) => {
                          if (index !== chartData.length - 1 || value == null) return null;
                          const txt = `Projected ${money(projection ?? value)}`;
                          const w = txt.length * 6.4 + 16;
                          // Forecast and Goal pace often finish near the same
                          // dollar level — when close, push this pill AWAY from
                          // the Goal pill (see below) so they don't overlap.
                          // Direction follows which value actually finishes
                          // higher, not a fixed up/down — otherwise a lower
                          // projection could render above a higher goal.
                          const rawOffset = labelsClose
                            ? (projectionEndsHigher ? viewBox.y - 22 : viewBox.y + 22)
                            : viewBox.y - 12;
                          // Clamp so the pill never runs off the top/bottom of
                          // the chart canvas — a goal/projection near the axis
                          // max (or min) was getting pushed above y=0 and cut off.
                          const yOffset = clampLabelY(rawOffset);
                          return (
                            <g>
                              <rect x={viewBox.x + 4} y={yOffset - 10} width={w} height={20} rx={10} fill="#F1EAFE" stroke="#D9CCFA" />
                              <text x={viewBox.x + 4 + w / 2} y={yOffset + 4} fill="#6D28D9" fontSize={11} fontWeight={700} textAnchor="middle">{txt}</text>
                            </g>
                          );
                        }}
                      />
                    )}
                    <Line
                      type="linear"
                      dataKey="goalPace"
                      name="Goal pace"
                      stroke="#8B93A3"
                      strokeWidth={1.5}
                      dot={false}
                      label={hasGoal ? ({ viewBox, value, index }) => {
                        if (index !== chartData.length - 1 || value == null) return null;
                        const txt = `Goal ${money(goal)}`;
                        const w = txt.length * 6.4 + 16;
                        // Mirror of the Forecast pill above: pushed opposite
                        // it when close, in whichever direction keeps this
                        // pill on the side that matches the actual $ order.
                        const rawOffset = labelsClose
                          ? (projectionEndsHigher ? viewBox.y + 22 : viewBox.y - 22)
                          : viewBox.y - 12;
                        const yOffset = clampLabelY(rawOffset);
                        return (
                          <g>
                            <rect x={viewBox.x + 4} y={yOffset - 10} width={w} height={20} rx={10} fill="#F3F4F7" stroke="#DDE1E8" />
                            <text x={viewBox.x + 4 + w / 2} y={yOffset + 4} fill="#5A6474" fontSize={11} fontWeight={700} textAnchor="middle">{txt}</text>
                          </g>
                        );
                      } : false}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : <div className="rc2-chart-empty">Revenue history is not available yet.</div>}
              {chartData.length > 0 && outlook.key !== 'neutral' && (
                <div className="rc2-chart-read">
                  <strong>Current read:</strong>{' '}
                  {outlook.key === 'risk'
                    ? 'Sales need to accelerate to reach the required daily pace.'
                    : outlook.key === 'achieved'
                      ? 'Goal reached — everything from here extends the result.'
                      : 'Sales are on pace for this month’s goal.'}
                </div>
              )}
            </div>
          </section>
        )}

        {healthData && (
          <section className={`rc2-health-brief rc2-health-brief--${outlook.key}`} aria-labelledby="health-brief-title">
            <div className="rc2-health-brief-main">
              <span className="rc2-decision-heading rc2-decision-heading--brief">{briefEyebrow}</span>
              <h2 id="health-brief-title">{briefHeadline}</h2>
              {recoveryCallout && <p className="rc2-recovery-callout">{recoveryCallout}</p>}
              {narrativeLoading && (
                <p className="rc2-health-brief-status">Preparing this week’s diagnosis from your latest customer signals…</p>
              )}
              {!narrativeLoading && hasNarrativeDiagnosis && (
                <p className="rc2-health-brief-diagnosis">{narrative.diagnosis}</p>
              )}
              {!narrativeLoading && !hasNarrativeDiagnosis && (
                <p className="rc2-health-brief-status">
                  This week’s AI diagnosis is not available yet. Revenue Plays still has the actions tied to this checkpoint.
                </p>
              )}
              {briefContext && <p className="rc2-health-brief-context">{briefContext}</p>}

              {watchedMetrics.length > 0 && (
              <div className="rc2-watching-grid rc2-watching-grid--visible" aria-label="Key signals">
                {watchedMetrics.map((metric) => (
                  <div className="rc2-watching-item" key={metric.label}>
                    <span>{metric.label}</span>
                    <strong>{metric.value}</strong>
                    {metric.note && <small>{metric.note}</small>}
                  </div>
                ))}
              </div>
              )}
            </div>

            <div className="rc2-health-brief-actions">
              {hasDetailSection && (
                <button
                  type="button"
                  className="rc2-diagnosis-toggle"
                  onClick={() => setIsDiagnosisExpanded((expanded) => !expanded)}
                  aria-expanded={isDiagnosisExpanded}
                  aria-controls="weekly-health-brief-detail"
                >
                  {isDiagnosisExpanded ? 'Hide full diagnosis' : 'View full diagnosis'}
                  {isDiagnosisExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>
              )}
              <button type="button" className="rc2-health-brief-plays" onClick={() => onNavigate?.('revenue')}>
                View Revenue Plays <ArrowRight size={16} />
              </button>
            </div>

            {isDiagnosisExpanded && hasDetailSection && (
              <div className="rc2-health-brief-detail" id="weekly-health-brief-detail">
                {hasNarrativeDetail && (
                  <div className="rc2-health-brief-explanation">
                    <h3>What’s happening</h3>
                    <p>{narrative.whats_happening}</p>
                  </div>
                )}
                {/* "What we're watching" removido (2026-08-08, decision de Alejo):
                    esas 3 señales ya viven como tarjetas propias abajo
                    (Purchase Rhythm, Repeat Behavior, Second Purchase) —
                    dentro del diagnostico eran duplicado. El espacio ganado
                    permite mostrar el diagnostico completo sin recortar. */}

                <div className="rc2-plays-summary">
                  <h3>Active Plays</h3>
                  {(!playsSummary || !playsAvailable) ? (
                    <p className="rc2-plays-empty">Plays status is not available right now.</p>
                  ) : activePlaysCount == null ? (
                    <p className="rc2-plays-empty">Plays status is not available right now.</p>
                  ) : activePlaysCount === 0 ? (
                    <p className="rc2-plays-empty">
                      No plays active yet.{' '}
                      <button type="button" className="rc2-inline-link" onClick={() => onNavigate?.('revenue')}>Activate one</button>
                    </p>
                  ) : (
                    <>
                      <p className="rc2-plays-line">
                        <strong>{number(activePlaysCount)}</strong> active play{activePlaysCount === 1 ? '' : 's'}
                        {previewPlayNames.length > 0 && (
                          <> — {previewPlayNames.join(', ')}{extraPlaysCount > 0 ? ` and ${extraPlaysCount} more` : ''}</>
                        )}
                      </p>
                      <p className="rc2-plays-revenue">
                        {totalCorrelatedRevenue == null
                          ? 'Revenue impact still forming (30-day window still open)'
                          : <>{money(totalCorrelatedRevenue)} correlated revenue, last 30 days</>}
                        {' '}
                        <span className="rc2-plays-attribution" title="These plays ran while this revenue came in — it's an association, not a guaranteed cause-and-effect result.">({attributionLabel})</span>
                      </p>
                      <p className="rc2-plays-attribution-note">Ran while this revenue came in — association, not proven cause.</p>
                    </>
                  )}
                </div>

                {topOpportunities.length > 0 && (
                  <div className="rc2-plays-opportunities">
                    <h3>Top opportunities this week</h3>
                    <ul className="rc2-plays-opp-list">
                      {topOpportunities.map((opp, idx) => (
                        <li key={opp.id ?? idx}>
                          <span className="rc2-plays-opp-title">{opp.title || 'Opportunity'}</span>
                          <strong className="rc2-plays-opp-amount">{opp.amount != null ? money(opp.amount) : '—'}</strong>
                        </li>
                      ))}
                    </ul>
                    <button type="button" className="rc2-inline-link rc2-plays-opp-cta" onClick={() => onNavigate?.('revenue')}>
                      View in Revenue Plays <ArrowRight size={13} />
                    </button>
                  </div>
                )}
              </div>
            )}
          </section>
        )}

        {healthData && (
          <section className="rc2-cycle" aria-labelledby="purchase-cycle-title">
            <div className="rc2-section-heading">
              <div><span className="rc2-eyebrow">Customer purchase cycle</span><h2 id="purchase-cycle-title">Customer Purchase Cycle</h2></div>
              <p>Signals that show how customers enter, return and move through their purchase rhythm.</p>
            </div>
            <div className="rc2-cycle-grid rc2-cycle-grid--flat">
              <CycleCard step="01" title="New customers">
                <strong className="rc2-primary-value">{detail.newBuyers30d?.toLocaleString() ?? '—'}</strong>
                <Delta value={detail.newBuyersChangePct} />
                <span className="rc2-benchmark">Last 30 days · Benchmark not available</span>
                <span
                  className="rc2-new-repeat-context"
                  title="Different from 12-Month Repeat Rate: this looks only at customers who are new (first purchase within the last 12 months)."
                >
                  {raw.new_customer_repeat_rate_12m != null
                    ? `${number(raw.new_customer_repeat_rate_12m * 100, 1)}% of new customers (last 12mo) have already bought again · based on ${number(raw.new_customer_repeat_rate_12m_cohort_size)} new customers`
                    : 'Not enough new customers yet to measure repeat rate'}
                </span>
                {raw.new_customer_repeat_rate_ytd != null && (
                  <span
                    className="rc2-new-repeat-context rc2-new-repeat-context--ytd"
                    title="Same idea, but only counting customers acquired since January 1st instead of a rolling 12-month window."
                  >
                    {`${number(raw.new_customer_repeat_rate_ytd * 100, 1)}% year-to-date (${number(raw.new_customer_repeat_rate_ytd_cohort_size)} new customers since Jan 1)`}
                  </span>
                )}
              </CycleCard>
              <CycleCard step="02" title="Time to second purchase" metrics={[t2sp, cadence, cohort60day]}>
                <span className="rc2-metric-name">Typical customer</span>
                <strong className="rc2-primary-value">{metricValue(t2sp)}</strong>
                <Delta value={null} lowerIsBetter />
                <span className="rc2-benchmark">{benchmarkLabel(t2sp)}</span>
                <div className="rc2-context-line"><span>Best customers (VIP) return in</span><strong>{metricValue(cadence)}</strong></div>
                <div className="rc2-context-line rc2-context-line--stacked">
                  <span>60-day return rate</span>
                  <strong>{metricValue(cohort60day)}</strong>
                  <small>{cohort60day?.value != null ? 'Of repeat buyers, this share made their second purchase within 60 days.' : 'Not enough data yet'}</small>
                </div>
              </CycleCard>
              <CycleCard step="03" title="Repeat behavior" metrics={[repeat12m, frequency]}>
                <span className="rc2-metric-name">12-month repeat rate</span>
                <strong className="rc2-primary-value">{metricValue(repeat12m)}</strong>
                <Delta value={null} unit="pts" />
                <span className="rc2-benchmark">{benchmarkLabel(repeat12m)}</span>
                <div className="rc2-paired-metrics">
                  <div><span>Purchase frequency</span><strong>{metricValue(frequency)}</strong></div>
                  <div><span>Repeat customers</span><strong>{number(raw.repeat_buyers_count)}</strong></div>
                </div>
              </CycleCard>
              <CycleCard step="04" title="AOV by customer type">
                {aovInsufficient ? (
                  <div className="rc2-aov-compare">
                    <div><span>New customers</span><strong>—</strong><small>Not enough data yet</small></div>
                    <div><span>Returning customers</span><strong>—</strong><small>Not enough data yet</small></div>
                  </div>
                ) : (
                  <div className="rc2-aov-compare">
                    <div><span>New customers</span><strong>{money(aovByType.aov_first_order)}</strong></div>
                    <div>
                      <span>Returning customers</span>
                      <strong>{money(aovByType.aov_repeat_orders)}</strong>
                      <small>{aovByType.aov_repeat_orders >= aovByType.aov_first_order ? '+' : ''}{money(aovByType.aov_repeat_orders - aovByType.aov_first_order)} vs. new</small>
                    </div>
                  </div>
                )}
                <div className="rc2-context-line"><span>Blended AOV</span><strong>{blendedAov ? money(blendedAov) : 'Not enough data yet'}</strong></div>
                <span className="rc2-benchmark">{(() => {
                  const s = aovByType?.sample_size;
                  const total = s ? Number(s.first_orders || 0) + Number(s.repeat_orders || 0) : null;
                  return !aovInsufficient && total ? `Based on ${number(total)} orders` : 'Split benchmarks not available';
                })()}</span>
              </CycleCard>
            </div>
            <article className={`rc2-rhythm-cycle-card ${atRiskCount > 0 ? 'rc2-rhythm-cycle-card--risk' : ''}`} aria-labelledby="rhythm-signal-title">
              <div className="rc2-rhythm-cycle-copy">
                <span className="rc2-decision-heading">How much customer value needs attention?</span>
                <h3 id="rhythm-signal-title">
                  {atRiskCount == null
                    ? 'Purchase rhythm signal is not available yet'
                    : atRiskCount > 0
                      ? `${number(atRiskCount)} customers are currently flagged at risk of not returning`
                      : 'No customers are currently flagged at risk of not returning'}
                </h3>
                <p>This early signal uses each customer’s purchase history and likelihood of returning. It does not mean they are lost.</p>
              </div>
              <div className="rc2-rhythm-facts rc2-rhythm-facts--cycle">
                <div>
                  <span>Of active customers currently flagged at risk</span>
                  <strong>{raw.at_risk_pct != null ? `${percent(raw.at_risk_pct)} currently flagged at risk` : 'Not enough data yet'}</strong>
                </div>
                <div>
                  <span>Estimated monthly customer value at risk</span>
                  <strong>{revenueAtRisk.total_clv != null ? money(revenueAtRisk.total_clv) : 'Not enough data yet'}</strong>
                </div>
              </div>
            </article>
          </section>
        )}

        {healthData && (
          <section className="rc2-support" aria-labelledby="supporting-title">
            <div className="rc2-section-heading rc2-section-heading--compact">
              <div><span className="rc2-eyebrow">Supporting metrics</span><h2 id="supporting-title">Customer value context</h2></div>
            </div>
            <div className="rc2-support-grid">
              <SupportingMetric label="CLV 12 months" metric={clv} />
              <SupportingMetric
                label="High-value customers"
                metric={highValue}
                note={highValueNote}
              />
              <SupportingMetric label="Historical repeat rate" metric={historicalRepeat} />
            </div>
          </section>
        )}

      </main>

      {healthData && !isChatOpen && (
        <button
          type="button"
          className="rc2-ask-ai-fab"
          onClick={openChat}
          aria-label="Ask the Segment Optimizer about these numbers"
        >
          <Sparkles size={18} strokeWidth={2.25} aria-hidden="true" />
          <span className="rc2-ask-ai-fab-label">Ask UPSELLerate</span>
        </button>
      )}

      {/* Segment Optimizer chat drawer — same component Revenue Finder uses.
          No lever context here (free questions); activation stays disabled on
          this screen, same decision as the old Checkpoint's metric chat. */}
      <SegmentOptimizerChat
        key={chatKey}
        isOpen={isChatOpen}
        onClose={() => setIsChatOpen(false)}
        token={propToken}
        companyId={companyId}
        onViewPlans={() => onNavigate?.('pricing')}
        allowActivate={false}
      />
    </div>
  );
}
